import { CONFIG } from "../config";
import { getUserIdFromToken, getUserRoleFromToken } from "./Auth";
import { userRoles } from "../types/enums";

/**
 * Static, per-country configuration for /bulk-order-operations.
 *
 * This module is deliberately frontend-only. The API stays dynamic and keeps its
 * broad `[Authorize(Roles = "Admin")]`; the narrowing to specific people, and the
 * per-country operational constants, live here alongside the other static config
 * (see menuOptions.ts).
 *
 * Countries are told apart by the API they point at. `CONFIG.BASE_URL` comes from
 * public/config.json, which docker-entrypoint.sh rewrites per DEPLOY_ZONE at
 * container start — so one build serves every country and this file is the only
 * place that has to know which is which.
 */

/** URL path segment for a category — `/bulk-order-operations/{key}`. */
export type BulkOperationCategoryKey =
	| "amk-orders"
	| "history-management"
	| "section-id"
	| "status-updates"
	| "duplicate-payments"
	| "client-reports";

interface CategorySettingsBase {
	enabled: boolean;
}

export interface AmkOrdersCategorySettings extends CategorySettingsBase {
	/**
	 * Users.Id the batches are attributed to: StatusHistory.UserId for the status
	 * operations, Orders.DeliveryDriverId for AddBalance. This is a stand-in
	 * account for the partner, not a real person — which is also why the backend
	 * is left to default DriverPrice to 0.
	 */
	amkDriverId: number;
	amkDriverName: string;
}

export interface HistoryManagementCategorySettings
	extends CategorySettingsBase {
	/**
	 * Responsible user written to StatusHistory.UserId for an INTERNATIONAL order —
	 * this deployment's OWN Postman account (Kosovo records Postman KS, Albania records
	 * Postman AL). That is the per-database system user; see
	 * `docs/api-documentations/devs/Order_Status_StatusHistory_International.md` §4,
	 * "UserId per database for system/bulk changes: KS = 1, AL = 2, MK = 3".
	 *
	 * The field is disabled in the UI for international orders and this id is sent
	 * instead of the operator's choice.
	 *
	 * Note this is intentionally a Client-role account; the API only rejects client
	 * accounts for LOCAL orders.
	 */
	internationalStatusUserId: number;
	internationalStatusUserName: string;
}

/** One entry in the target-country dropdown of the Section ID category. */
export interface SectionIdTargetCountry {
	/** 1 KS · 2 AL · 3 MK. Display and ordering only — the API never sees it. */
	countryId: number;
	/** Key inside the `sectionId.countries` localization block. */
	labelKey: string;
	fallbackLabel: string;
	/**
	 * dbo.Zones.Id of a SECTION (LevelId = 3) IN THIS DEPLOYMENT'S DATABASE.
	 *
	 * Zone ids are database-local: 336 is Tiranë in PostmanAL and means something
	 * else (or nothing) in PostmanKS. So these are configured per API URL and must
	 * never be copied between the country blocks below. The API rejects an id that
	 * is not a level-3 zone, which catches a typo before any order is touched.
	 */
	sectionId: number;
	/** Zone name, shown next to the picker and in the confirm dialog. */
	sectionName: string;
}

export interface SectionIdCategorySettings extends CategorySettingsBase {
	countries: SectionIdTargetCountry[];
}

/**
 * Bulk Status Updates carries no settings of its own. Its international responsible user is
 * read from the `history-management` block on purpose — it is the same Postman system account
 * for the same reason, and duplicating the id here would create a second place to get it wrong.
 */
export type StatusUpdatesCategorySettings = CategorySettingsBase;

/**
 * Duplicate Payments carries no settings of its own. The date-range cap is a backend rule
 * mirrored in MAX_DUPLICATE_RANGE_DAYS below, not a per-country choice, and the category has
 * no driver and no zone ids.
 */
export type DuplicatePaymentsCategorySettings = CategorySettingsBase;

/**
 * Client Reports carries no settings of its own. The three reports are read-only, behave
 * identically against all three databases, and have no driver id, zone id or money movement to
 * configure — the country columns come back from the API already split.
 */
export type ClientReportsCategorySettings = CategorySettingsBase;

/** Each category carries its own settings — they have nothing in common but `enabled`. */
export interface CategorySettingsByKey {
	"amk-orders": AmkOrdersCategorySettings;
	"history-management": HistoryManagementCategorySettings;
	"section-id": SectionIdCategorySettings;
	"status-updates": StatusUpdatesCategorySettings;
	"duplicate-payments": DuplicatePaymentsCategorySettings;
	"client-reports": ClientReportsCategorySettings;
}

export interface CountryBulkOpsConfig {
	/** Users.Id values allowed to open the module at all. */
	allowedUserIds: number[];
	categories: CategorySettingsByKey;
}

/** Backend cap, mirrored here so the UI can reject a paste before sending it. */
export const MAX_ORDER_IDS = 5000;

/**
 * Widest date range the Duplicate Payments search accepts, in whole calendar days. Mirrors
 * DuplicatePaymentService.MaxRangeDays — dbo.OrderPayment has no index on CreatedAt, so every
 * search is a table scan and the cap is what keeps repeated clicking off the buffer pool.
 */
export const MAX_DUPLICATE_RANGE_DAYS = 3;

const CONFIG_BY_API_URL: Record<string, CountryBulkOpsConfig> = {
	"https://apiks.postman-ks.com": {
		allowedUserIds: [
			1, // postmanks
		],
		categories: {
			"amk-orders": {
				enabled: true,
				amkDriverId: 14314,
				amkDriverName: "Amk Driver",
			},
			"history-management": {
				enabled: true,
				// Kosovo records its own Postman KS account (this db's system user).
				internationalStatusUserId: 1,
				internationalStatusUserName: "Postman KS",
			},
			// PostmanKS zone ids. These happen to be the same values as the Albania
			// block below, but they are still read per database on purpose — nothing
			// keeps the two zone trees in sync, so do not collapse them into one list.
			"section-id": {
				enabled: true,
				countries: [
					{
						countryId: 1,
						labelKey: "countryKs",
						fallbackLabel: "Kosovë",
						sectionId: 21,
						sectionName: "",
					},
					{
						countryId: 2,
						labelKey: "countryAl",
						fallbackLabel: "Shqipëri",
						sectionId: 336,
						sectionName: "Tiranë",
					},
					{
						countryId: 3,
						labelKey: "countryMk",
						fallbackLabel: "Maqedoni",
						sectionId: 316,
						sectionName: "",
					},
				],
			},
			// No settings of its own — the international responsible user comes from
			// the history-management block above.
			"status-updates": {
				enabled: true,
			},
			// No settings of its own — the 3-day search cap is a backend rule, not a
			// per-country choice.
			"duplicate-payments": {
				enabled: true,
			},
			// Read-only reports, no per-country constants. See ClientReportsCategorySettings.
			"client-reports": {
				enabled: true,
			},
		},
	},
	// Kosovo dev deployment. Same values as the production KS block above — it
	// points at a restore of that database.
	"https://apiks-dev.postman-ks.com": {
		allowedUserIds: [
			1, // postmanks
		],
		categories: {
			"amk-orders": {
				enabled: true,
				amkDriverId: 14314,
				amkDriverName: "Amk Driver",
			},
			"history-management": {
				enabled: true,
				// Kosovo records its own Postman KS account (this db's system user).
				internationalStatusUserId: 1,
				internationalStatusUserName: "Postman KS",
			},
			"section-id": {
				enabled: true,
				countries: [
					{
						countryId: 1,
						labelKey: "countryKs",
						fallbackLabel: "Kosovë",
						sectionId: 21,
						sectionName: "",
					},
					{
						countryId: 2,
						labelKey: "countryAl",
						fallbackLabel: "Shqipëri",
						sectionId: 336,
						sectionName: "Tiranë",
					},
					{
						countryId: 3,
						labelKey: "countryMk",
						fallbackLabel: "Maqedoni",
						sectionId: 316,
						sectionName: "",
					},
				],
			},
			// No settings of its own — the international responsible user comes from
			// the history-management block above.
			"status-updates": {
				enabled: true,
			},
			// Mirrors the production KS block above, per this entry's own note: dev points at a
			// restore of that database. The key is REQUIRED by CategorySettingsByKey - main added
			// the category to the three blocks that existed there, and never saw this fourth one.
			"duplicate-payments": {
				enabled: true,
			},
			// Read-only reports, no per-country constants. See ClientReportsCategorySettings.
			"client-reports": {
				enabled: true,
			},
		},
	},
	"https://apial.postman-ks.com": {
		allowedUserIds: [
			2, // postmanal
		],
		categories: {
			"amk-orders": {
				enabled: true,
				amkDriverId: 2940,
				amkDriverName: "AMK Albania",
			},
			"history-management": {
				enabled: true,
				// Albania records its own Postman AL account (this db's system user).
				internationalStatusUserId: 2,
				internationalStatusUserName: "Postman AL",
			},
			// PostmanAL zone ids. 336 (Tiranë) is the value the hand-run script has
			// been using.
			"section-id": {
				enabled: true,
				countries: [
					{
						countryId: 1,
						labelKey: "countryKs",
						fallbackLabel: "Kosovë",
						sectionId: 21,
						sectionName: "",
					},
					{
						countryId: 2,
						labelKey: "countryAl",
						fallbackLabel: "Shqipëri",
						sectionId: 336,
						sectionName: "Tiranë",
					},
					{
						countryId: 3,
						labelKey: "countryMk",
						fallbackLabel: "Maqedoni",
						sectionId: 316,
						sectionName: "",
					},
				],
			},
			// No settings of its own — the international responsible user comes from
			// the history-management block above.
			"status-updates": {
				enabled: true,
			},
			// No settings of its own — the 3-day search cap is a backend rule, not a
			// per-country choice.
			"duplicate-payments": {
				enabled: true,
			},
			// Read-only reports, no per-country constants. See ClientReportsCategorySettings.
			"client-reports": {
				enabled: true,
			},
		},
	},
	// Albania dev deployment. Same values as the production AL block above — it points at a
	// restore of that database, exactly like the Kosovo dev entry. Without this key the URL is
	// unrecognised, getBulkOpsConfig() returns null, and the whole module disappears.
	"https://apial-dev.postman-ks.com": {
		allowedUserIds: [
			2, // postmanal
		],
		categories: {
			"amk-orders": {
				enabled: true,
				amkDriverId: 2940,
				amkDriverName: "AMK Albania",
			},
			"history-management": {
				enabled: true,
				// Albania records its own Postman AL account (this db's system user).
				internationalStatusUserId: 2,
				internationalStatusUserName: "Postman AL",
			},
			// PostmanAL zone ids. 336 (Tiranë) is the value the hand-run script has
			// been using.
			"section-id": {
				enabled: true,
				countries: [
					{
						countryId: 1,
						labelKey: "countryKs",
						fallbackLabel: "Kosovë",
						sectionId: 21,
						sectionName: "",
					},
					{
						countryId: 2,
						labelKey: "countryAl",
						fallbackLabel: "Shqipëri",
						sectionId: 336,
						sectionName: "Tiranë",
					},
					{
						countryId: 3,
						labelKey: "countryMk",
						fallbackLabel: "Maqedoni",
						sectionId: 316,
						sectionName: "",
					},
				],
			},
			// No settings of its own — the international responsible user comes from
			// the history-management block above.
			"status-updates": {
				enabled: true,
			},
			// No settings of its own — the 3-day search cap is a backend rule, not a
			// per-country choice.
			"duplicate-payments": {
				enabled: true,
			},
			// Read-only reports, no per-country constants. See ClientReportsCategorySettings.
			"client-reports": {
				enabled: true,
			},
		},
	},
	// Local development only (SpeedApi's "http" launch profile). Delete this entry
	// to lock dev out too. Dev-DB user ids differ from production — add your own
	// admin id here, do not assume the production ids below apply.
	"http://localhost:5224": {
		allowedUserIds: [
			1, // postmanks — replace with your dev-DB admin id
		],
		categories: {
			"amk-orders": {
				enabled: true,
				amkDriverId: 14314,
				amkDriverName: "Amk Driver",
			},
			"history-management": {
				enabled: true,
				// Kosovo records its own Postman KS account (this db's system user).
				internationalStatusUserId: 1,
				internationalStatusUserName: "Postman KS",
			},
			// Production ids, on the assumption the dev DB is a restore of prod. If
			// yours is not, point these at your own zones — the API rejects anything
			// that is not a level-3 zone, so a wrong id fails loudly rather than
			// silently moving orders somewhere unexpected.
			"section-id": {
				enabled: true,
				countries: [
					{
						countryId: 1,
						labelKey: "countryKs",
						fallbackLabel: "Kosovë",
						sectionId: 21,
						sectionName: "",
					},
					{
						countryId: 2,
						labelKey: "countryAl",
						fallbackLabel: "Shqipëri",
						sectionId: 336,
						sectionName: "Tiranë",
					},
					{
						countryId: 3,
						labelKey: "countryMk",
						fallbackLabel: "Maqedoni",
						sectionId: 316,
						sectionName: "",
					},
				],
			},
			// No settings of its own — the international responsible user comes from
			// the history-management block above.
			"status-updates": {
				enabled: true,
			},
			// No settings of its own — the 3-day search cap is a backend rule, not a
			// per-country choice.
			"duplicate-payments": {
				enabled: true,
			},
			// Read-only reports, no per-country constants. See ClientReportsCategorySettings.
			"client-reports": {
				enabled: true,
			},
		},
	},
};

const normalizeUrl = (url: string) =>
	url.trim().toLowerCase().replace(/\/+$/, "");

/**
 * Config for the country this build points at, or null when the API URL is not
 * recognised. Unknown URL means no access — an unconfigured deployment must not
 * fall back to another country's driver id.
 */
export const getBulkOpsConfig = (): CountryBulkOpsConfig | null => {
	const baseUrl = CONFIG?.BASE_URL;
	if (typeof baseUrl !== "string") {
		return null;
	}
	return CONFIG_BY_API_URL[normalizeUrl(baseUrl)] ?? null;
};

/**
 * Whether the signed-in user may open the module. Admin role AND an explicit
 * allowlist entry — the role alone is too broad for a page that moves money.
 */
export const canAccessBulkOrderOperations = (): boolean => {
	if (getUserRoleFromToken() !== userRoles.ADMIN) {
		return false;
	}
	const userId = getUserIdFromToken();
	if (typeof userId !== "number") {
		return false;
	}
	return getBulkOpsConfig()?.allowedUserIds.includes(userId) ?? false;
};

/** Generic over the key so each caller gets that category's own settings type. */
export const getCategoryConfig = <K extends BulkOperationCategoryKey>(
	key: K
): CategorySettingsByKey[K] | null =>
	getBulkOpsConfig()?.categories[key] ?? null;

/** Category keys enabled for this country, in declaration order. */
export const getEnabledCategoryKeys = (): BulkOperationCategoryKey[] => {
	const config = getBulkOpsConfig();
	if (!config) {
		return [];
	}
	return (
		Object.keys(config.categories) as BulkOperationCategoryKey[]
	).filter((key) => config.categories[key].enabled);
};
